import React, { useEffect, useRef, useState } from 'react'
import BabysittersProfileModel from './Components/BabysitterProfileModel'
import ConsumersForBabysitter from './Components/ConsumersForBabysitter'
import CustomPageNavigator from '@/components/Pagination'
import DataUnavailable from '@/components/DataUnavailable'
import Loader from '@/components/Loader'
import { RegisteredBabysitterTable } from './Components/RegisteredBabysitterTable'
import StyledButton from '@/components/Button/Button'
import { invalidateQuery } from '@/services/config/queryClient'
import { UserInterface } from '@/services/types/APIResponseType'
import cacheKeys from '@/services/cacheKeys'
import useGetRegisteredBabySitter from '@/services/hooks/useGetRegisteredBabySitter'
import usePostBlockBabysitter from '@/services/hooks/usePostBlockBabysitter'
import { usePostApproveBabysitter } from '@/services/hooks/usePostApproveBabysitter'
import usePostRegisteredConsumersCount from '@/services/hooks/usePostRegisteredConsumersCount'
import './RegisteredBabysitter.css'
import { styled, alpha } from '@mui/material/styles'
import { Backdrop, Box, Fade, Modal, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import InputBase from '@mui/material/InputBase'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

type ApprovalDialogPayload = {
  userId: string
  approvalStatus: 'approved' | 'rejected'
}

const RegisteredBabysitter = () => {
  const [babysitters, setBabysitters] = useState<UserInterface[] | undefined>()
  const [consumersCount, setConsumersCount] = useState<number | undefined>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [open, setOpen] = React.useState<boolean>(false)
  const [selectedBabysitter, setSelectedBabysitter] =
    useState<UserInterface | null>(null)
  const [showConsumers, setShowConsumers] = useState<boolean>(false)
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState('')
  const [debouncedValue, setDebouncedValue] = useState(searchValue)
  const [approvalConfirm, setApprovalConfirm] =
    useState<ApprovalDialogPayload | null>(null)
  const approvalDialogSnapshotRef = useRef<ApprovalDialogPayload | null>(null)

  const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginLeft: 0,
    width: '50%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
      width: 'auto',
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  }))

  const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }))

  const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    width: '100%',
    '& .MuiInputBase-input': {
      padding: theme.spacing(1, 1, 1, 0),
      paddingLeft: `calc(1em + ${theme.spacing(4)})`,
      transition: theme.transitions.create('width'),
      [theme.breakpoints.up('sm')]: {
        width: '23ch',
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  }))

  const queryParams = {
    limit: 5,
    page: currentPage,
    search: debouncedValue,
  }

  const { data: getRegisteredBabySitter, isLoading } =
    useGetRegisteredBabySitter(queryParams)
  const registeredBabysitter = getRegisteredBabySitter?.data

  const { mutate: blockBabysitter } = usePostBlockBabysitter()
  const { mutate: approveBabysitter } = usePostApproveBabysitter()
  const { mutate: registeredBabysitterCount } =
    usePostRegisteredConsumersCount()

  const handleClose = () => {
    setOpen(false)
    setConsumersCount(undefined)
  }

  const handleClickBack = (showConsumers: boolean) => {
    setShowConsumers(showConsumers)
  }

  const handleOpen = (babysitterData: UserInterface) => {
    setOpen(true)
    setSelectedBabysitter(babysitterData)
    const requestedData = {
      userId: babysitterData?.profileInfo?.userId,
    }
    registeredBabysitterCount(requestedData, {
      onSuccess: (response) => {
        setConsumersCount(response?.data?.count)
      },
    })
  }

  const handleShowConsumer = () => {
    setOpen(false)
    setConsumersCount(undefined)
    setShowConsumers(true)
  }

  const handleStatusChange = (isBlocked: boolean) => {
    const requestedData = {
      userId: selectedBabysitter?.profileInfo?.userId || '',
      isAccountBlocked: isBlocked,
    }

    if (requestedData.userId) {
      blockBabysitter(requestedData, {
        onSuccess: () => {
          setShowPopup(false)
          invalidateQuery([cacheKeys.REGISTERED_BABYSITTERS])
        },
      })
    }
  }

  const handleApproval = (
    userId: string,
    approvalStatus: 'approved' | 'rejected',
  ) => {
    const payload: ApprovalDialogPayload = { userId, approvalStatus }
    approvalDialogSnapshotRef.current = payload
    setApprovalConfirm(payload)
  }

  const handleApprovalDialogClose = (): void => {
    setApprovalConfirm(null)
  }

  const handleApprovalDialogExited = (): void => {
    approvalDialogSnapshotRef.current = null
  }

  const handleApprovalConfirm = () => {
    if (!approvalConfirm) return
    approveBabysitter(approvalConfirm, {
      onSuccess: () => {
        setApprovalConfirm(null)
        invalidateQuery([cacheKeys.REGISTERED_BABYSITTERS])
      },
    })
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }

  useEffect(() => {
    setBabysitters(registeredBabysitter?.babysittersProfiles)
  }, [registeredBabysitter])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchValue)
    }, 400)

    return () => {
      clearTimeout(handler)
    }
  }, [searchValue])

  if (isLoading) {
    return <Loader />
  }

  const approvalDialogDisplay: ApprovalDialogPayload | null =
    approvalConfirm ?? approvalDialogSnapshotRef.current

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'end',
          marginRight: '20px',
          marginTop: '20px',
        }}
      >
        <div></div>
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Search…"
            inputProps={{ 'aria-label': 'search' }}
            value={searchValue}
            onChange={handleSearchChange}
            autoFocus
          />
        </Search>
      </div>
      {!showConsumers ? (
        <div>
          {registeredBabysitter && registeredBabysitter?.totalCount > 0 ? (
            <>
              <RegisteredBabysitterTable
                currentItems={babysitters || []}
                handleOnClickOpen={handleOpen}
                handleApproval={handleApproval}
              />
              <CustomPageNavigator
                currentPage={currentPage}
                totalItemCount={registeredBabysitter?.totalCount || 0}
                limitPerPage={queryParams.limit}
                setCurrentPage={setCurrentPage}
              />
              <BabysittersProfileModel
                isModelOpen={open}
                profileData={selectedBabysitter!}
                handleModelClose={handleClose}
                consumersCount={consumersCount!}
                showPopup={showPopup}
                handleStatusChange={handleStatusChange}
                handleShowConsumersData={handleShowConsumer}
              />
              <Modal
                open={!!approvalConfirm}
                onClose={handleApprovalDialogClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 500 } }}
              >
                <Fade
                  in={!!approvalConfirm}
                  onExited={handleApprovalDialogExited}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: { xs: '85%', sm: '400px' },
                      color: 'white',
                      border: '1px solid rgb(75, 90, 135)',
                      borderRadius: '20px',
                      background: 'rgba(43, 36, 96, 0.05)',
                      boxShadow: '0 0 24px rgba(0, 0, 0, 0.1)',
                      backdropFilter: 'blur(28px)',
                      padding: '40px 30px',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '16px',
                    }}
                  >
                    {approvalDialogDisplay?.approvalStatus === 'approved' ? (
                      <CheckCircleOutlineIcon
                        sx={{
                          fontSize: '56px',
                          color: 'rgba(52, 199, 89, 1)',
                        }}
                      />
                    ) : approvalDialogDisplay?.approvalStatus === 'rejected' ? (
                      <HighlightOffIcon
                        sx={{ fontSize: '56px', color: 'rgb(253, 12, 4)' }}
                      />
                    ) : null}
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: '#fffffc' }}
                    >
                      {approvalDialogDisplay?.approvalStatus === 'approved'
                        ? 'Approve Supervisor'
                        : approvalDialogDisplay?.approvalStatus === 'rejected'
                          ? 'Reject Supervisor'
                          : ''}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#848484', mb: 1 }}
                    >
                      {approvalDialogDisplay?.approvalStatus === 'approved'
                        ? 'Are you sure you want to approve this supervisor?'
                        : approvalDialogDisplay?.approvalStatus === 'rejected'
                          ? 'Are you sure you want to reject this supervisor?'
                          : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '12px' }}>
                      <StyledButton
                        onClick={handleApprovalConfirm}
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid #FB5012',
                          color: '#FB5012',
                          padding: '10px 28px',
                        }}
                      >
                        Yes
                      </StyledButton>
                      <StyledButton
                        onClick={handleApprovalDialogClose}
                        style={{ padding: '10px 28px' }}
                      >
                        No
                      </StyledButton>
                    </Box>
                  </Box>
                </Fade>
              </Modal>
            </>
          ) : (
            <DataUnavailable description="No record found" />
          )}
        </div>
      ) : (
        <ConsumersForBabysitter
          babysitterId={selectedBabysitter?.profileInfo.userId || ''}
          onClickBack={handleClickBack}
        />
      )}
    </>
  )
}

export default RegisteredBabysitter
